import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Grid,
  Group,
  NumberInput,
  Paper,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { PiDotsSixVertical, PiPlus, PiTrash } from 'react-icons/pi';
import { LineRef, lineKey } from '@/models/lineKey';
import { TOffer } from '@/models/offers';
import PricingHeader from './PricingHeader';
import {
  applyPercentage,
  DraftInstallment,
  money,
  OrderedService,
  SplitDerived,
} from './splitDerive';

const METHODS = ['Bank Transfer', 'Cryptocurrency'];
const POOL_ID = 'pool';

export default function SplitScheduleBuilder({
  installments,
  setInstallments,
  editable,
  services,
  currency,
  derived,
  offer,
  showCogs = true,
  showAssignment = true,
  showPricingHeader = true,
  showNote = false,
  assignEditable,
}: {
  installments: DraftInstallment[];
  setInstallments: (next: DraftInstallment[]) => void;
  editable: boolean;
  services: OrderedService[];
  currency: string;
  /** Account the offer belongs to. Retained for the prop contract shared with the
   *  BizDev counter-proposal editor. */
  accountId: number;
  derived: SplitDerived;
  /** Present in the SR/AcceptedSplit context (carries the Discount field into
   *  the pricing header). Absent in the BizDev counter-proposal context. */
  offer?: TOffer;
  /** COGS figures are for BizDev review only — hidden in the SR builder. */
  showCogs?: boolean;
  /** Service-to-installment assignment (drag/drop + pool) is a BizDev concern —
   *  hidden in the SR builder, which plans amounts only. */
  showAssignment?: boolean;
  /** The pricing/COGS strip. Off where the surrounding page already carries those
   *  figures — the BizDev review screen puts them in its sticky scoreboard. */
  showPricingHeader?: boolean;
  /** The per-installment note. BizDev's to write (`canAssign`), and the only thing the
   *  customer is told about an installment, so an SR sees it read-only where one
   *  exists. Off in the SR's own builder, where no note has been written yet. */
  showNote?: boolean;
  /** Whether services can be dragged, independently of whether the schedule itself can
   *  be edited. BizDev reviewing an SR's proposal distributes the services but must not
   *  touch the amounts, so that panel passes `editable={false}` with this on. Defaults
   *  to `editable`. */
  assignEditable?: boolean;
}) {
  const d = derived;
  const canAssign = assignEditable ?? editable;
  // Writing the note is BizDev's alone, and `assignEditable` is passed only by the
  // BizDev review panel. Deriving it from `editable` instead would hand the field to
  // the SR in their own planner.
  const noteEditable = assignEditable === true;
  const sorted = [...installments].sort((a, b) => a.seq - b.seq);

  // Map a draggable id (lineKey) back to its LineRef.
  const refByKey = useMemo(() => {
    const m = new Map<string, LineRef>();
    for (const s of services) m.set(lineKey(s.lineRef), s.lineRef);
    return m;
  }, [services]);

  const patch = (key: string, fields: Partial<DraftInstallment>) =>
    setInstallments(
      installments.map((i) => (i.key === key ? { ...i, ...fields } : i)),
    );

  const add = () => {
    const seq = installments.length
      ? Math.max(...installments.map((i) => i.seq)) + 1
      : 1;
    setInstallments([
      ...installments,
      {
        key: `new-${Date.now()}-${seq}`,
        seq,
        currency,
        paymentMethod: METHODS[0],
        payerId: null,
        amount: null,
        lines: [],
      },
    ]);
  };

  const remove = (key: string) =>
    setInstallments(
      installments
        .filter((i) => i.key !== key)
        .sort((a, b) => a.seq - b.seq)
        .map((i, idx) => ({ ...i, seq: idx + 1 })),
    );

  // Move a line to installment `key`, or to the pool when `key` is null.
  const assign = (ref: LineRef, key: string | null) => {
    const lk = lineKey(ref);
    setInstallments(
      installments.map((i) => ({
        ...i,
        lines:
          i.key === key
            ? [...i.lines.filter((r) => lineKey(r) !== lk), ref]
            : i.lines.filter((r) => lineKey(r) !== lk),
      })),
    );
  };

  const setFirstToCogs = () => {
    if (sorted.length === 0) return;
    const firstKey = sorted[0].key;
    setInstallments(
      installments.map((i) => ({
        ...i,
        lines: i.key === firstKey ? services.map((s) => s.lineRef) : [],
      })),
    );
  };

  const assignmentByKey = new Map<string, string>();
  for (const i of installments)
    for (const ref of i.lines) assignmentByKey.set(lineKey(ref), i.key);
  const unassignedServices = services.filter(
    (s) => !assignmentByKey.has(lineKey(s.lineRef)),
  );

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!canAssign) return;
    if (!destination || destination.droppableId === source.droppableId) return;
    const ref = refByKey.get(draggableId);
    if (!ref) return;
    assign(
      ref,
      destination.droppableId === POOL_ID ? null : destination.droppableId,
    );
  };

  const installmentsList =
    sorted.length === 0 ? (
      <Paper withBorder p="lg" data-testid="ux-empty-installments-hint">
        <Stack gap="sm" align="center">
          <Text fz="sm" c="dimmed" ta="center">
            {showAssignment
              ? 'No installments yet. Add one, then drag each service into it.'
              : 'No installments yet. Add one and set its amount.'}
          </Text>
          {editable && (
            <Button
              size="xs"
              leftSection={<PiPlus size={14} />}
              onClick={add}
              data-testid="ux-add-installment-empty"
            >
              Add installment
            </Button>
          )}
        </Stack>
      </Paper>
    ) : (
      <Stack gap="sm">
        {sorted.map((inst, idx) => {
          const isFirst = idx === 0;
          const isCogsCovering = isFirst && d.cogsCovered;
          const assigned = services.filter(
            (s) => assignmentByKey.get(lineKey(s.lineRef)) === inst.key,
          );
          return (
            <Paper key={inst.key} withBorder p="xs">
              <Stack gap={6}>
                <Group
                  justify="space-between"
                  wrap="nowrap"
                  gap="xs"
                  align="center"
                >
                  <Group gap={6} wrap="nowrap">
                    <Badge variant="filled" color="gray" radius="sm" size="sm">
                      Inst. {inst.seq}
                    </Badge>
                    {showCogs && isCogsCovering && (
                      <Tooltip label="Covers the full order COGS — an unpaid later installment is a minimal loss.">
                        <Badge size="sm" variant="light" color="indigo">
                          COGS-covering
                        </Badge>
                      </Tooltip>
                    )}
                  </Group>
                  <Group gap="xs" wrap="nowrap" align="center">
                    <AmountPercent
                      value={d.saleByInst[inst.key] || 0}
                      orderedTotal={d.orderedTotal}
                      currency={currency}
                      editable={editable}
                      onChange={(amount) => patch(inst.key, { amount })}
                      onPercent={(pct) =>
                        setInstallments(
                          applyPercentage(
                            installments,
                            d.saleByInst,
                            inst.key,
                            pct,
                            d.orderedTotal,
                          ),
                        )
                      }
                    />
                    {showCogs && (
                      <Text fz="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                        COGS {money(d.cogsByInst[inst.key] || 0, currency)}
                      </Text>
                    )}
                    {editable && (
                      <Tooltip label="Remove installment">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          onClick={() => remove(inst.key)}
                        >
                          <PiTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                </Group>

                {showNote &&
                  (noteEditable ? (
                    <TextInput
                      size="xs"
                      placeholder="What this installment covers — shown to the customer"
                      value={inst.note ?? ''}
                      maxLength={500}
                      onChange={(e) =>
                        patch(inst.key, { note: e.currentTarget.value })
                      }
                      data-testid={`ux-installment-note-${inst.seq}`}
                    />
                  ) : (
                    inst.note && (
                      <Text
                        fz="xs"
                        c="dimmed"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {inst.note}
                      </Text>
                    )
                  ))}

                {showAssignment && (
                  <Droppable droppableId={inst.key} isDropDisabled={!canAssign}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          minHeight: 32,
                          borderRadius: 6,
                          padding: 4,
                          background: snapshot.isDraggingOver
                            ? 'var(--mantine-color-blue-light)'
                            : undefined,
                        }}
                      >
                        <Stack gap={4}>
                          {assigned.length === 0 && (
                            <Text fz="xs" c="dimmed" ta="center" py={4}>
                              {canAssign
                                ? 'Drag related services here (optional).'
                                : 'No services.'}
                            </Text>
                          )}
                          {assigned.map((svc, i) => (
                            <ServiceDraggable
                              key={lineKey(svc.lineRef)}
                              svc={svc}
                              index={i}
                              currency={currency}
                              editable={canAssign}
                              showCogs={showCogs}
                            />
                          ))}
                          {provided.placeholder}
                        </Stack>
                      </Box>
                    )}
                  </Droppable>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );

  return (
    <Box>
      {/* One aligned pricing header row: figures (left) + discount & actions (right). */}
      {showPricingHeader && (
        <PricingHeader
          derived={d}
          currency={currency}
          offer={offer}
          // "Set inst. 1 = COGS" is an assignment shortcut, so it follows the drag
          // permission; adding an installment changes the schedule, so it follows the
          // schedule permission. In the SR builder the two are the same flag.
          editable={canAssign}
          showAddInstallment={editable}
          showCogs={showCogs}
          onSetFirstToCogs={setFirstToCogs}
          onAddInstallment={add}
          canSetFirstToCogs={sorted.length > 0}
        />
      )}

      {showAssignment ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Grid gap="md" mt="xs">
            {/* Left: installments */}
            <Grid.Col span={{ base: 12, md: canAssign ? 7 : 12 }}>
              {installmentsList}
            </Grid.Col>

            {/* Right: services still to place (only when they can be moved) */}
            {canAssign && (
              <Grid.Col span={{ base: 12, md: 5 }}>
                {/* The pool is the work queue: it stays in reach while the reviewer
                    scrolls a long list of installments. */}
                <Paper
                  withBorder
                  p="sm"
                  style={{ position: 'sticky', top: 64 }}
                  data-testid="ux-unassigned-pool"
                >
                  <Group justify="space-between" mb="xs" wrap="nowrap">
                    <Text fw={500} fz="sm">
                      Services to assign
                    </Text>
                    <Group gap="xs" wrap="nowrap">
                      {!showPricingHeader && canAssign && sorted.length > 0 && (
                        <Tooltip label="Assign every service to installment 1 so it covers the full order COGS">
                          <Button
                            size="compact-xs"
                            variant="light"
                            onClick={setFirstToCogs}
                          >
                            Set inst. 1 = COGS
                          </Button>
                        </Tooltip>
                      )}
                      <Badge
                        size="sm"
                        variant="light"
                        color={
                          unassignedServices.length === 0 ? 'teal' : 'orange'
                        }
                      >
                        {unassignedServices.length}
                      </Badge>
                    </Group>
                  </Group>
                  <Droppable droppableId={POOL_ID} isDropDisabled={!canAssign}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          minHeight: 80,
                          borderRadius: 6,
                          padding: 4,
                          background: snapshot.isDraggingOver
                            ? 'var(--mantine-color-blue-light)'
                            : undefined,
                        }}
                      >
                        <Stack gap={4}>
                          {unassignedServices.length === 0 && (
                            <Text fz="xs" c="dimmed" py="sm" ta="center">
                              All services assigned.
                            </Text>
                          )}
                          {unassignedServices.map((svc, i) => (
                            <ServiceDraggable
                              key={lineKey(svc.lineRef)}
                              svc={svc}
                              index={i}
                              currency={currency}
                              editable={canAssign}
                              showCogs={showCogs}
                            />
                          ))}
                          {provided.placeholder}
                        </Stack>
                      </Box>
                    )}
                  </Droppable>
                  {sorted.length === 0 && (
                    <Text fz="xs" c="dimmed" mt={4}>
                      Add an installment first, then drag each service into it.
                    </Text>
                  )}
                </Paper>
              </Grid.Col>
            )}
          </Grid>
        </DragDropContext>
      ) : (
        <Box mt="xs">{installmentsList}</Box>
      )}
    </Box>
  );
}

/**
 * Editable billed amount for an installment, with a linked % of the order total.
 * Amount is the source of truth; typing either field patches the amount (the %
 * back-computes to `orderedTotal * pct / 100`). Read-only mode shows a figure + badge.
 */
function AmountPercent({
  value,
  orderedTotal,
  currency,
  editable,
  onChange,
  onPercent,
}: {
  value: number;
  orderedTotal: number;
  currency: string;
  editable: boolean;
  onChange: (amount: number) => void;
  onPercent: (pct: number) => void;
}) {
  const pct = orderedTotal > 0 ? (value / orderedTotal) * 100 : 0;

  if (!editable) {
    return (
      <Group gap={6} wrap="nowrap">
        <Text fz="sm" fw={500} style={{ whiteSpace: 'nowrap' }}>
          {money(value, currency)}
        </Text>
        <Badge size="sm" variant="light" color="gray">
          {Math.round(pct)}%
        </Badge>
      </Group>
    );
  }

  return (
    <Group gap={4} wrap="nowrap">
      <NumberInput
        size="xs"
        w={124}
        min={0}
        hideControls
        thousandSeparator=","
        value={Math.round(value * 100) / 100}
        onChange={(v) => onChange(typeof v === 'number' ? v : Number(v) || 0)}
        rightSection={
          <Text fz="xs" c="dimmed">
            {currency}
          </Text>
        }
        rightSectionWidth={40}
        styles={{ input: { textAlign: 'right', paddingRight: 42 } }}
        data-testid="ux-installment-amount-input"
      />
      <NumberInput
        size="xs"
        w={68}
        min={0}
        max={100}
        hideControls
        suffix="%"
        decimalScale={1}
        value={Math.round(pct * 10) / 10}
        onChange={(v) => {
          const p = typeof v === 'number' ? v : Number(v) || 0;
          onPercent(p);
        }}
      />
    </Group>
  );
}

function ServiceDraggable({
  svc,
  index,
  currency,
  editable,
  showCogs,
}: {
  svc: OrderedService;
  index: number;
  currency: string;
  editable: boolean;
  showCogs: boolean;
}) {
  return (
    <Draggable
      draggableId={lineKey(svc.lineRef)}
      index={index}
      isDragDisabled={!editable}
    >
      {(provided, snapshot) => {
        const card = (
          <Paper
            ref={provided.innerRef}
            {...provided.draggableProps}
            withBorder
            p={6}
            bg={
              snapshot.isDragging
                ? 'var(--mantine-color-blue-light)'
                : undefined
            }
          >
            <Group justify="space-between" gap="xs" wrap="nowrap">
              <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                <Box
                  {...provided.dragHandleProps}
                  style={{
                    cursor: editable ? 'grab' : 'not-allowed',
                    touchAction: 'none',
                    display: 'flex',
                  }}
                >
                  <PiDotsSixVertical
                    size={16}
                    color="var(--mantine-color-gray-5)"
                  />
                </Box>
                <Box style={{ minWidth: 0 }}>
                  <Text fz="xs" truncate>
                    {svc.name}
                  </Text>
                  {(svc.cpName || svc.blockTitle) && (
                    <Text fz="xs" c="dimmed" truncate>
                      {svc.cpName ?? svc.blockTitle}
                    </Text>
                  )}
                </Box>
              </Group>
              <Group gap="md" wrap="nowrap">
                <Text fz="xs" style={{ whiteSpace: 'nowrap' }}>
                  {money(svc.saleValue, currency)}
                </Text>
                {showCogs && (
                  <Group gap={2} wrap="nowrap">
                    <Text fz="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      {money(svc.cogs, currency)}
                    </Text>
                    {svc.cogsEstimated && (
                      <Tooltip label="COGS estimated (rate/agent data incomplete)">
                        <Text fz="xs" c="orange">
                          ~
                        </Text>
                      </Tooltip>
                    )}
                  </Group>
                )}
              </Group>
            </Group>
          </Paper>
        );
        // While dragging, portal to <body> so the Drawer's CSS transform doesn't
        // offset the fixed-positioned drag clone from the cursor.
        return snapshot.isDragging ? createPortal(card, document.body) : card;
      }}
    </Draggable>
  );
}
